/**
 * キャリコネクション（グローバルウェイ）登録連携の varuna エンドポイント クライアント。
 *
 * 画面や composable から axios を生で叩かず、エンドポイント定義をここに集約する
 * （recommend-jobs-client.ts と同じ作法。新規エンドポイントは JSON）。
 *
 * - `careerconnection_alliances/`  … ①媒体取得（表示前。非対象も 200 + eligible:false）
 * - `send_data_careerconnection/` … ②登録（同意後）
 * - `careerconnection_hope_masters/` … 本人希望欄の希望勤務地・転職時期の選択肢と、住所→勤務地コードの判定
 */
import axios from 'axios'
import type { CcMedia } from '@/utils/realtime-transfer/careerconnectionSnapshot'
import type {
  HopeTurnTime,
  HopeWorkArea
} from '@/utils/format-edit/hopeWorkCondition'

const ENDPOINT_PREFIX = process.env.VUE_APP_ENDPOINT_PREFIX || '/'

/** ①は表示直前に呼ぶ。varuna→proxy 9 秒 + 余裕 */
const ALLIANCES_TIMEOUT_MS = 15000
/** ②はビズリーチ送客と同じ 60 秒 */
const SEND_TIMEOUT_MS = 60000
/** 選択肢の取得（DB を読むだけ） */
const HOPE_MASTERS_TIMEOUT_MS = 15000

const url = (path: string) => `${ENDPOINT_PREFIX}${path}`

/**
 * varuna は両エンドポイントで Firebase ID トークン（Authorization: Bearer）を要求する
 * （無し→401、token の uid ≠ body.uuid→403）。呼び出し側が取得済みのトークンを渡す
 */
const postJson = <T>(
  path: string,
  body: unknown,
  timeout: number,
  idToken: string
) =>
  axios.post<T>(url(path), body, {
    timeout,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`
    }
  })

export interface CcAlliancesResponse {
  success: boolean
  eligible: boolean
  reasons: string[]
  media: CcMedia | null
  candidate_alliance_keys: string[]
}

export interface CcSendResponse {
  result: 'ok' | 'error'
  cc_user_id?: string | null
  alliance_key?: string
  accepted_alliance_keys?: string[]
  dry_run?: boolean
  payload?: Record<string, unknown>
  errors?: unknown
}

/** ①媒体取得。ネットワーク例外は呼び出し側で「非対象」に丸める */
export async function postAlliances(
  body: Record<string, unknown>,
  idToken: string
): Promise<CcAlliancesResponse> {
  const res = await postJson<Partial<CcAlliancesResponse>>(
    'careerconnection_alliances/',
    body,
    ALLIANCES_TIMEOUT_MS,
    idToken
  )
  const data = res.data || {}
  const media = data.media && typeof data.media === 'object' ? data.media : null
  return {
    success: data.success !== false,
    eligible: data.eligible === true && !!media && !!media.alliance_key,
    reasons: Array.isArray(data.reasons) ? data.reasons.map(String) : [],
    media,
    candidate_alliance_keys: Array.isArray(data.candidate_alliance_keys)
      ? data.candidate_alliance_keys.map(String)
      : []
  }
}

/** ②登録。4xx/5xx は axios が例外を投げるので extractCcError で本文を取り出す */
export async function postSend(
  body: Record<string, unknown>,
  idToken: string
): Promise<CcSendResponse> {
  const res = await postJson<CcSendResponse>(
    'send_data_careerconnection/',
    body,
    SEND_TIMEOUT_MS,
    idToken
  )
  return res.data || { result: 'error' }
}

export interface CcHopeMastersResponse {
  success: boolean
  workAreas: HopeWorkArea[]
  turnTimes: HopeTurnTime[]
  resolvedAreaCodes: Record<string, string | null>
}

const str = (value: unknown) => (value == null ? '' : String(value))

/**
 * 本人希望欄の選択肢（キャリコネ rt_condition の additional_dialog_info）と、
 * texts（住所・旧形式の勤務地）それぞれの先方勤務地コード。ログイン前でも呼べる（トークン不要）
 */
export async function postHopeMasters(
  texts: string[]
): Promise<CcHopeMastersResponse> {
  const res = await axios.post(
    url('careerconnection_hope_masters/'),
    { texts },
    {
      timeout: HOPE_MASTERS_TIMEOUT_MS,
      headers: { 'Content-Type': 'application/json' }
    }
  )
  const data = (res.data || {}) as Record<string, unknown>
  const list = (value: unknown) =>
    (Array.isArray(value) ? value : []) as Record<string, unknown>[]
  const resolvedRaw =
    data.resolvedAreaCodes && typeof data.resolvedAreaCodes === 'object'
      ? (data.resolvedAreaCodes as Record<string, unknown>)
      : {}
  const resolvedAreaCodes: Record<string, string | null> = {}
  Object.keys(resolvedRaw).forEach((key) => {
    resolvedAreaCodes[key] = resolvedRaw[key] ? str(resolvedRaw[key]) : null
  })
  return {
    success: data.success === true,
    workAreas: list(data.workAreas)
      .map((a) => ({
        code: str(a.code),
        label: str(a.label),
        regionCode: str(a.regionCode),
        regionLabel: str(a.regionLabel)
      }))
      .filter((a) => a.code && a.label),
    turnTimes: list(data.turnTimes)
      .map((t) => ({ code: str(t.code), label: str(t.label) }))
      .filter((t) => t.code && t.label),
    resolvedAreaCodes
  }
}

const GENERIC_ERROR = '送信に失敗しました。時間をおいて再度お試しください。'
/** タイムアウト・通信断（応答なし）。先方では登録が済んでいることがある */
const UNCERTAIN_ERROR =
  '通信が途切れたため、送信できたか確認できませんでした。時間をおいてから再度お試しください。'
const NOT_ELIGIBLE_ERROR =
  '履歴書の内容が送信の条件を満たしていないため、送信できませんでした。入力内容をご確認ください。'
const STALE_SELECTION_ERROR =
  '表示から時間が経ったため送信できませんでした。お手数ですが、もう一度お試しください。'

/** そのまま画面に出してよい文言か（HTML の本文や長い開発者向け文言は出さない） */
function toUserMessage(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const text = value.trim()
  if (text === '' || text.length > 200 || /[<>]/.test(text)) return null
  // ①の選択との不一致は開発者向けの言い回しなので言い換える
  if (text.includes('alliance_key')) return STALE_SELECTION_ERROR
  return text
}

/**
 * 失敗レスポンスからユーザー向けメッセージを作る（英語の axios 文言は出さない）。
 * varuna は 400/409/502 を `{result:'error', errors: string | {field: msg} | {reasons, missing}}` で返す。
 */
export function extractCcError(e: unknown): string {
  const err = e as { response?: { data?: CcSendResponse } } | null
  const data = err && err.response ? err.response.data : undefined
  const errors = data && typeof data === 'object' ? data.errors : undefined
  const direct = toUserMessage(errors)
  if (direct) return direct
  if (errors && typeof errors === 'object') {
    const values = Object.values(errors as Record<string, unknown>)
    for (const value of values) {
      const message = toUserMessage(value)
      if (message) return message
    }
    const record = errors as Record<string, unknown>
    if (Array.isArray(record.reasons) || Array.isArray(record.missing)) {
      return NOT_ELIGIBLE_ERROR
    }
  }
  if (!err || !err.response) return UNCERTAIN_ERROR
  return GENERIC_ERROR
}
